// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable } from '@angular/core';
import { KeyProject, ViewState } from '@lfx/key-projects/key-projects.model';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { downloadCSVFile } from '@lfx/core/helper';

/**
 * CSV column configuration for easy extensibility
 * Add or remove columns by modifying this interface
 */
export interface CsvRow extends Record<string, string> {
  'Project Name': string;
  'Combined Health Score': string;
  'Health: Contributors': string;
  'Health: Popularity': string;
  'Health: Development': string;
  'Health: Security': string;
  'Technical Influence': string;
  'Ecosystem Influence': string;
  'Ecosystem Influence Level': string;
  'Participant Count': string;
  'Activity Count': string;
}

/**
 * Service responsible for exporting key projects data to CSV format
 *
 * Follows Clean Architecture principles:
 * - Single Responsibility: Handles only CSV export logic
 * - Open/Closed: Easy to extend with new columns via CsvRow interface
 * - Testable: Pure functions with no side effects (except downloadCSVFile)
 *
 * @example
 * ```typescript
 * constructor(private csvExportService: KeyProjectsCsvExportService) {}
 *
 * exportData() {
 *   this.csvExportService.exportToCsv(projects, viewState);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class KeyProjectsCsvExportService {
  /**
   * Export key projects to CSV file
   *
   * @param projects - Array of key projects to export
   * @param viewState - Current view state for filename generation
   * @returns The number of exported projects
   */
  exportToCsv(projects: KeyProject[], viewState: ViewState | null): number {
    if (!projects || projects.length === 0) {
      return 0;
    }

    const csvData = this.transformToCsvData(projects);
    const filename = this.generateFilename(viewState);

    downloadCSVFile(csvData, filename);

    return projects.length;
  }

  /**
   * Transform key projects data to CSV-friendly format
   *
   * This method encapsulates all business logic for CSV data transformation:
   * - Health score extraction and formatting
   * - Activity trend calculation
   * - Null-safe field access
   *
   * @param projects - Array of key projects
   * @returns Array of CSV row objects
   */
  transformToCsvData(projects: KeyProject[]): CsvRow[] {
    return projects.map(project => this.transformSingleProject(project));
  }

  /**
   * Transform a single project to CSV row
   * Extracted for better testability and maintainability
   *
   * @param project - Single key project
   * @returns CSV row object
   */
  private transformSingleProject(project: KeyProject): CsvRow {
    return {
      'Project Name': this.getProjectName(project),
      'Combined Health Score': this.getHealthScore(project),
      'Health: Contributors': this.getHealthContributors(project),
      'Health: Popularity': this.getHealthPopularity(project),
      'Health: Development': this.getHealthDevelopment(project),
      'Health: Security': this.getHealthSecurity(project),
      'Technical Influence': this.getTechnicalInfluence(project),
      'Technical Influence Level': this.getTechnicalInfluenceLevel(project),
      'Ecosystem Influence': this.getEcosystemInfluence(project),
      'Ecosystem Influence Level': this.getEcosystemInfluenceLevel(project),
      'Participant Count': this.getContributorCount(project),
      'Activity Count': this.getActivityCount(project),
    };
  }

  private getTechnicalInfluenceLevel(project: KeyProject): string {
    const levelName = project.technicalInfluenceMetrics.technicalInfluenceLevelName;
    if (levelName === 'Unknown') {
        return 'Non-LF Project';
    }

    return levelName;
  }

  private getEcosystemInfluenceLevel(project: KeyProject): string {
    const levelName = project.ecosystemInfluenceMetrics.ecosystemInfluenceLevelName;
    if (levelName === 'Unknown') {
        return 'Non-LF Project';
    }

    return levelName;
  }

  private getActivityCount(project: KeyProject): string {
    return project.activityCount.toString();
  }

  private getHealthContributors(project: KeyProject): string {
    return `${project.healthScore.contributorPercentage}%`;
  }

  private getHealthPopularity(project: KeyProject): string {
    return `${project.healthScore.popularityPercentage}%`;
  }

  private getHealthDevelopment(project: KeyProject): string {
    return `${project.healthScore.developmentPercentage}%`;
  }

  private getHealthSecurity(project: KeyProject): string {
    return `${project.healthScore.securityPercentage}%`;
  }

  /**
   * Generate filename based on current view state
   *
   * Formats:
   * - All projects: "key-projects-YYYY-MM-DD.csv"
   * - Workspace: "workspace-{name}-projects-YYYY-MM-DD.csv"
   *
   * @param viewState - Current view state
   * @returns Generated filename with timestamp
   */
  generateFilename(viewState: ViewState | null): string {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    let filename = 'key-projects';

    if (viewState?.mode === 'workspace-projects' && viewState.selectedWorkspace) {
      const workspaceName = this.sanitizeFilename(viewState.selectedWorkspace.name);
      filename = `workspace-${workspaceName}-projects`;
    }

    return `${filename}-${timestamp}.csv`;
  }

  /**
   * Sanitize workspace name for safe filename usage
   * Removes special characters and replaces spaces with hyphens
   *
   * @param name - Workspace name
   * @returns Sanitized filename-safe string
   */
  private sanitizeFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  }

  // ============================================================================
  // Field Extractors - Easy to extend with new transformations
  // ============================================================================

  private getProjectName(project: KeyProject): string {
    return project.name || 'Unknown Project';
  }

  private getHealthScore(project: KeyProject): string {
    const healthScoreValue = KeyProjectsHelper.extractHealthScoreValue(project.healthScore);
    const healthScoreLevel = KeyProjectsHelper.mapHealthScoreToLevel(healthScoreValue);
    const healthScoreText = KeyProjectsHelper.getHealthScoreDisplayText(healthScoreLevel.level);

    return `${healthScoreText} (${healthScoreValue}/100)`;
  }

  private getTechnicalInfluence(project: KeyProject): string {
    return `${project.technicalInfluenceMetrics.totalPoints} points`;
  }

  private getEcosystemInfluence(project: KeyProject): string {
    return `${project.ecosystemInfluenceMetrics.totalPoints} points`;
  }

  private getContributorCount(project: KeyProject): string {
    return (project.contributorCount || 0).toString();
  }

}

